import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { countdown, fmtTime, isDone, mapsHref, gapMinutes } from "../lib/liveUtils";
import { LightArc, dayFraction } from "../components/LightArc";
import { toast } from "sonner";
import {
  ArrowLeft, Plane, BedDouble, LogOut, Car, Ticket, Copy, MapPin, Phone,
  CheckCircle2, Plus, X, Loader2, Hourglass,
} from "lucide-react";

const KIND_ICONS = {
  flight: Plane, hotel_checkin: BedDouble, hotel_checkout: LogOut,
  car_pickup: Car, car_dropoff: Car, event: Ticket,
};

const EXPENSE_CATEGORIES = ["food", "transport", "lodging", "activities", "shopping", "misc"];

function copyConf(conf) {
  navigator.clipboard.writeText(conf)
    .then(() => toast.success("Confirmation number copied"))
    .catch(() => toast.error("Could not copy"));
}

function dayLabel(iso) {
  const d = new Date(`${iso}T12:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
}

function fmtGap(mins) {
  const h = Math.floor(mins / 60), m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function ItemCard({ item, done, isNext, now }) {
  const Icon = KIND_ICONS[item.kind] || Ticket;
  const cd = isNext ? countdown(item.ts, item.local, now) : null;
  return (
    <div className={`relative pl-12 pb-6 transition-opacity ${done ? "opacity-45" : ""}`} data-testid={`live-item-${item.id}`}>
      <div className={`absolute left-[13px] top-1 w-5 h-5 flex items-center justify-center border-2 bg-surface z-10
        ${isNext ? "border-ember" : done ? "border-sand-300" : "border-sand-400"}`}>
        {done ? <CheckCircle2 size={12} className="text-sand-400" /> : <Icon size={11} className={isNext ? "text-ember" : "text-sand-500"} />}
      </div>
      <div className={`border bg-surface p-4 ${isNext ? "border-ember border-2 shadow-[4px_4px_0_#C15A38]/20" : "border-sand-200"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-mono text-xs text-sand-500">
              {fmtTime(item.local)}{item.tz ? ` ${item.tz}` : ""}{item.time_estimated ? " (est.)" : ""}
              {item.kind === "flight" && item.end_local ? ` → ${fmtTime(item.end_local)}${item.end_tz ? ` ${item.end_tz}` : ""}` : ""}
            </p>
            <p className="font-medium text-sm mt-0.5 truncate">{item.title}</p>
            {item.subtitle && <p className="text-xs text-sand-500 truncate">{item.subtitle}</p>}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            {cd && cd !== "now" && (
              <span className="font-mono text-[10px] font-semibold uppercase tracking-wide px-2 py-1 bg-ember text-white" data-testid="live-next-countdown">{cd}</span>
            )}
            {item.planned && (
              <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800">planned</span>
            )}
          </div>
        </div>

        {item.detail_rows?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5">
            {item.detail_rows.map(([k, v]) => (
              <p key={k} className="text-[11px] text-sand-600"><span className="font-mono text-sand-400 uppercase text-[9px]">{k}</span> {v}</p>
            ))}
          </div>
        )}

        {(item.confirmation || item.maps_query || item.phone) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {item.confirmation && (
              <button onClick={() => copyConf(item.confirmation)}
                className="flex items-center gap-1.5 border border-sand-300 px-2 py-1 font-mono text-xs font-semibold hover:border-ember hover:text-ember"
                data-testid={`live-conf-${item.id}`}>
                {item.confirmation} <Copy size={10} className="text-sand-400" />
              </button>
            )}
            {item.maps_query && (
              <a href={mapsHref(item.maps_query)} target="_blank" rel="noreferrer"
                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-ember hover:underline px-1 py-1"
                data-testid={`live-maps-${item.id}`}>
                <MapPin size={11} /> Maps
              </a>
            )}
            {item.phone && (
              <a href={`tel:${item.phone}`}
                className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-ember hover:underline px-1 py-1"
                data-testid={`live-call-${item.id}`}>
                <Phone size={11} /> Call
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuickExpense({ tripId, onClose }) {
  const [category, setCategory] = useState("food");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/trips/${tripId}/expenses`, {
        category, label: label.trim(), amount: Number(amount),
        date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Expense logged");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save expense");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="quick-expense-dialog">
      <form onSubmit={save} className="bg-surface border-2 border-ink w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-display font-medium tracking-tight">Log expense</p>
          <button type="button" onClick={onClose} className="text-sand-400 hover:text-ink" data-testid="quick-expense-close"><X size={16} /></button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {EXPENSE_CATEGORIES.map((c) => (
            <button key={c} type="button" onClick={() => setCategory(c)}
              className={`font-mono text-[10px] uppercase tracking-wide px-2 py-1 border ${category === c ? "bg-ember text-white border-ember" : "border-sand-300 text-sand-600"}`}
              data-testid={`quick-expense-cat-${c}`}>
              {c}
            </button>
          ))}
        </div>
        <input required placeholder="What was it? (e.g. Taxi to hotel)" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
          className="w-full border border-sand-300 px-3 py-2 text-sm mb-2 focus:outline-none focus:border-ember" data-testid="quick-expense-label" />
        <input required type="number" step="0.01" min="0.01" placeholder="Amount (USD)" value={amount} onChange={(e) => setAmount(e.target.value)}
          className="w-full border border-sand-300 px-3 py-2 text-sm font-mono mb-4 focus:outline-none focus:border-ember" data-testid="quick-expense-amount" />
        <button type="submit" disabled={busy} className="btn-swiss btn-primary w-full text-sm disabled:opacity-50" data-testid="quick-expense-save">
          {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Save expense
        </button>
      </form>
    </div>
  );
}

export default function LiveTrip() {
  const { tripId } = useParams();
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [light, setLight] = useState(null);

  useEffect(() => {
    api.get(`/trips/${tripId}/live`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load live itinerary"));
    api.get(`/trips/${tripId}/light`).then(({ data }) => setLight(data)).catch(() => {});
  }, [tripId]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  const { days, nextId } = useMemo(() => {
    const items = data?.items || [];
    let nextId = null;
    for (const it of items) {
      if (!isDone(it, now)) { nextId = it.id; break; }
    }
    const days = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const day = it.local.slice(0, 10);
      if (!days.length || days[days.length - 1].day !== day) days.push({ day, entries: [] });
      const prev = i > 0 && items[i - 1].local.slice(0, 10) === day ? items[i - 1] : null;
      const gap = prev ? gapMinutes(prev, it) : null;
      days[days.length - 1].entries.push({ item: it, gap });
    }
    return { days, nextId };
  }, [data, now]);

  if (!data) {
    return (
      <div>
        <main className="max-w-2xl mx-auto px-6 py-10"><div className="h-64 bg-surface rounded-card border border-hairline animate-pulse" /></main>
      </div>
    );
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const active = data.trip.start_date <= todayIso && data.trip.end_date >= todayIso;

  return (
    <div>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-28" data-testid="live-trip-page">
        <Link to={`/trips/${tripId}`} className="inline-flex items-center text-sm text-sand-600 hover:text-ember mb-6" data-testid="live-back">
          <ArrowLeft size={14} className="mr-1.5" /> {data.trip.name}
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="heading-h1 text-4xl sm:text-5xl">Live.</h1>
          {active && (
            <span className="flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-ember" data-testid="live-badge">
              <span className="w-2 h-2 bg-ember rounded-full animate-pulse" /> On trip
            </span>
          )}
        </div>
        <p className="font-mono text-xs text-sand-500 mb-4">
          {data.trip.start_date} → {data.trip.end_date}{data.trip.destination ? ` · ${data.trip.destination}` : ""}
        </p>

        {active && (
          <div className="mb-8 bg-surface rounded-card border border-hairline px-4 pt-4 pb-3" data-testid="live-daylight">
            <LightArc fraction={light?.available ? light.day_fraction : dayFraction(now)} dotBorder="var(--surface)" />
            <div className="flex justify-between mt-2 font-mono text-[10px] tracking-wider text-ink-faint">
              <span>{light?.available ? `SUNRISE ${light.sunrise}` : "06:00"}</span>
              <span className="text-ember">NOW {light?.available ? light.now_local : now.toTimeString().slice(0, 5)}</span>
              <span>{light?.available ? `SUNSET ${light.sunset}` : "20:00"}</span>
            </div>
            {light?.available && (
              <p className="mt-1.5 font-mono text-[10px] tracking-wider" style={{ color: "var(--hour-deep)" }} data-testid="live-golden-hour">
                ◐ BEST LIGHT {light.golden_start}–{light.golden_end}{light.place ? ` · ${light.place.toUpperCase()}` : ""}
              </p>
            )}
          </div>
        )}

        {days.length === 0 ? (
          <div className="bg-surface rounded-card border border-hairline p-10 text-center" data-testid="live-empty">
            <p className="font-display font-medium text-xl text-sand-300">Nothing scheduled</p>
            <p className="text-sm text-sand-500 mt-1">Add reservations to this trip to see the live timeline.</p>
          </div>
        ) : (
          days.map(({ day, entries }) => (
            <section key={day} className="mb-2" data-testid={`live-day-${day}`}>
              <div className="flex items-center gap-3 mb-4">
                <p className={`font-mono text-[11px] font-bold tracking-[0.2em] ${day === todayIso ? "text-ember" : "text-sand-400"}`}>
                  {dayLabel(day)}{day === todayIso ? " · TODAY" : ""}
                </p>
                <div className="flex-1 h-px bg-sand-200" />
              </div>
              <div className="relative">
                <div className="absolute left-[22px] top-0 bottom-0 w-px bg-sand-200" />
                {entries.map(({ item, gap }) => (
                  <div key={item.id}>
                    {gap >= 60 && (
                      <p className="pl-12 pb-4 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-sand-400" data-testid={`live-gap-${item.id}`}>
                        <Hourglass size={10} /> {fmtGap(gap)} free
                      </p>
                    )}
                    {item.id === nextId && (
                      <div className="relative pl-12 pb-3" data-testid="live-now-marker">
                        <div className="absolute left-0 right-0 top-1.5 h-px bg-ember/60" />
                        <span className="absolute left-1 -top-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-ember bg-paper pr-1">Now</span>
                      </div>
                    )}
                    <ItemCard item={item} done={isDone(item, now)} isNext={item.id === nextId} now={now} />
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      <button onClick={() => setExpenseOpen(true)}
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 btn-swiss btn-primary shadow-lg flex items-center gap-2 text-sm"
        data-testid="quick-expense-btn">
        <Plus size={15} /> Expense
      </button>
      {expenseOpen && <QuickExpense tripId={tripId} onClose={() => setExpenseOpen(false)} />}
    </div>
  );
}
